import type { WorkCollaborationPreference } from './onboarding.js';
import type { ConsultantCredentialsStatus } from './profile-credentials.js';
import type { ProfileCompletenessDto } from './profile-completeness.js';

export interface ExpertiseDto {
  id: string;
  name: string;
}

export interface IndustryDto {
  id: string;
  name: string;
}

export interface ProfileCertificationDto {
  name: string;
  issuer?: string | null;
  year?: number | null;
}

export const ConsultantSeniorityValues = [
  'junior',
  'mid',
  'senior',
  'principal',
  'partner',
] as const;

export type ConsultantSeniority = (typeof ConsultantSeniorityValues)[number];

export const LanguageProficiencyValues = [
  'native',
  'full_professional',
  'professional_working',
  'limited_working',
  'elementary',
] as const;

export type LanguageProficiency = (typeof LanguageProficiencyValues)[number];

/** ISO 639-1 codes allowed for profile language skills (spoken/working languages). */
export const LanguageSkillCodeValues = [
  'cs',
  'en',
  'de',
  'sk',
  'pl',
  'fr',
  'es',
  'it',
  'uk',
  'ru',
] as const;

export type LanguageSkillCode = (typeof LanguageSkillCodeValues)[number];

export interface ProfileLanguageSkillDto {
  languageCode: LanguageSkillCode;
  proficiency: LanguageProficiency;
}

/** Higher rank = stronger proficiency (native = highest). */
export function languageProficiencyRank(proficiency: LanguageProficiency): number {
  const index = LanguageProficiencyValues.indexOf(proficiency);
  if (index === -1) {
    return 0;
  }
  return LanguageProficiencyValues.length - index;
}

export function findDuplicateLanguageSkillCode(
  skills: readonly ProfileLanguageSkillDto[],
): string | null {
  const seen = new Set<string>();
  for (const skill of skills) {
    if (seen.has(skill.languageCode)) {
      return skill.languageCode;
    }
    seen.add(skill.languageCode);
  }
  return null;
}

export function normalizeLanguageSkillsForStorage(
  skills: ProfileLanguageSkillDto[] | undefined,
): ProfileLanguageSkillDto[] | null | undefined {
  if (skills === undefined) {
    return undefined;
  }
  return skills.length > 0 ? skills : null;
}

export const ProfileSkillMaxCount = 30;
export const ProfileSkillLabelMaxLength = 80;

export interface ProfileSkillDto {
  id: string;
  name: string;
}

export function normalizeProfileSkillIdsForStorage(
  skillIds: string[] | undefined,
): string[] | null | undefined {
  if (skillIds === undefined) {
    return undefined;
  }
  return skillIds.length > 0 ? skillIds : null;
}

export interface ProfileDto {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  description: string | null;
  jobTitle: string | null;
  photoUrl: string | null;
  expertises: ExpertiseDto[];
  primaryExpertiseIds: string[];
  industries: IndustryDto[];
  yearsOfExperience: number | null;
  consultantSeniority: ConsultantSeniority | null;
  certifications: ProfileCertificationDto[];
  languageSkills: ProfileLanguageSkillDto[];
  skills: ProfileSkillDto[];
  workCollaborationPreferences: WorkCollaborationPreference[] | null;
  linkedinUrl: string | null;
  portfolioUrl: string | null;
  consultantCredentialsStatus: ConsultantCredentialsStatus;
  consultantCredentialsVerifiedAt: string | null;
  /** Present for consultant / solution-provider accounts when returned from GET /me/profile. */
  completeness?: ProfileCompletenessDto;
}

export interface UpdateProfileDto {
  firstName?: string;
  lastName?: string;
  description?: string;
  jobTitle?: string;
  workCollaborationPreferences?: WorkCollaborationPreference[];
  linkedinUrl?: string | null;
  portfolioUrl?: string | null;
  yearsOfExperience?: number | null;
  consultantSeniority?: ConsultantSeniority | null;
  certifications?: ProfileCertificationDto[];
  languageSkills?: ProfileLanguageSkillDto[];
  /** Catalog skill ids and/or new label strings; resolved server-side on save. */
  skills?: string[];
}

export interface UpdateExpertisesDto {
  expertiseIds: string[];
  primaryExpertiseIds?: string[];
}

export interface UpdateIndustriesDto {
  industryIds: string[];
}

export interface TeamMemberDto {
  id: string;
  firstName: string;
  lastName: string;
}
